import type { Category, Confession, ConfessionFeedResponse, ReactionType } from '../types/confession';
import { getInitDataRaw } from './telegram';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  let initData: string | undefined;
  try {
    initData = getInitDataRaw();
  } catch (e) {
    console.warn('Failed to get init data:', e);
    initData = undefined;
  }
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(initData && { 'X-Telegram-Init-Data': initData }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
    cache: 'no-store', // Prevent browser caching
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

export type SortOption = 'new' | 'hot' | 'top';
export type TimePeriod = 'day' | 'week' | 'all';

export async function getConfessions(params: {
  category?: Category | 'all';
  cursor?: string;
  limit?: number;
  daily?: boolean;
  sort?: SortOption;
  period?: TimePeriod;
}): Promise<ConfessionFeedResponse> {
  const searchParams = new URLSearchParams();
  
  if (params.category && params.category !== 'all') {
    searchParams.set('category', params.category);
  }
  if (params.cursor) {
    searchParams.set('cursor', params.cursor);
  }
  if (params.limit) {
    searchParams.set('limit', params.limit.toString());
  }
  if (params.daily) {
    searchParams.set('daily', 'true');
  }
  if (params.sort) {
    searchParams.set('sort', params.sort);
  }
  if (params.period && params.period !== 'all') {
    searchParams.set('period', params.period);
  }

  const query = searchParams.toString();
  return apiRequest<ConfessionFeedResponse>(`/confessions${query ? `?${query}` : ''}`);
}

export async function getConfession(id: string): Promise<Confession> {
  return apiRequest<Confession>(`/confessions/${id}`);
}

export async function getRandomConfession(): Promise<Confession> {
  return apiRequest<Confession>('/confessions/random');
}

export async function submitConfession(data: {
  text: string;
  category: Category;
}): Promise<Confession> {
  return apiRequest<Confession>('/confessions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface Comment {
  id: string;
  agentName: string;
  text: string;
  trigger: string;
  framework: string;
  createdAt: string;
  reactions: {
    laugh: number;
    mindblown: number;
    cringe: number;
    skull: number;
  };
  userReactions: ReactionType[];
}

export async function getComments(
  confessionId: string,
  params?: {
    sort?: 'top' | 'new';
    limit?: number;
    offset?: number;
  }
): Promise<{ comments: Comment[]; count: number; totalCount: number; hasMore: boolean }> {
  const searchParams = new URLSearchParams();
  if (params?.sort) searchParams.set('sort', params.sort);
  if (params?.limit) searchParams.set('limit', params.limit.toString());
  if (params?.offset) searchParams.set('offset', params.offset.toString());
  
  const query = searchParams.toString();
  return apiRequest<{ comments: Comment[]; count: number; totalCount: number; hasMore: boolean }>(
    `/confessions/${confessionId}/comments${query ? `?${query}` : ''}`
  );
}

export async function reactToComment(
  commentId: string,
  reaction: ReactionType,
  remove: boolean = false
): Promise<{ success: boolean; reactions: Record<ReactionType, number> }> {
  return apiRequest(`/comments/${commentId}/react`, {
    method: 'POST',
    body: JSON.stringify({ reaction, remove }),
  });
}

export async function reactToConfession(
  confessionId: string,
  reaction: ReactionType,
  remove: boolean = false
): Promise<{ success: boolean; reactions: Record<ReactionType, number> }> {
  return apiRequest(`/confessions/${confessionId}/react`, {
    method: 'POST',
    body: JSON.stringify({ reaction, remove }),
  });
}

export async function getDailyTop(): Promise<Confession[]> {
  const response = await getConfessions({ daily: true, limit: 10 });
  return response.confessions;
}

export async function shareConfessionWithImage(confessionId: string): Promise<{ success: boolean; message: string }> {
  return apiRequest(`/confessions/${confessionId}/share`, {
    method: 'POST',
  });
}

// No caching — each share attempt needs a fresh prepared_message_id with unique hash
// This is the key fix: reusing the same ID causes Telegram's share button to get stuck

export async function prepareShareConfession(confessionId: string): Promise<{ 
  success: boolean; 
  prepared_message_id?: string;
  image_url?: string;
  expiration_date?: number;
  error?: string;
  details?: string;
}> {
  try {
    // Always request a fresh prepared_message_id (unique hash generated server-side)
    const initData = getInitDataRaw();
    console.log('[prepareShareConfession] Requesting fresh prepared_message_id for', confessionId);
    
    const response = await fetch(`${API_BASE}/confessions/${confessionId}/prepare-share`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(initData && { 'X-Telegram-Init-Data': initData }),
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      console.error('[prepareShareConfession] Backend error:', data);
      return {
        success: false,
        error: data.error || 'Request failed',
        details: data.details,
      };
    }
    
    console.log(`[prepareShareConfession] Got fresh prepared_message_id for ${confessionId}`);
    return data;
  } catch (error) {
    console.error('[prepareShareConfession] Network error:', error);
    return {
      success: false,
      error: 'Network error',
      details: String(error),
    };
  }
}

// No-op — cache removed since each share needs a fresh unique ID
export function clearPreparedMessageCache(_confessionId?: string) {
  // Intentionally empty — no cache to clear
}
